<template>
    <Card>
	    <template #content>
        <div class="flex flex-col gap-8">
	        <FormGroup :label="$t('forms.device_model.device_model_properties_form.name')" :error-text="form.errors?.name">
            <InputText
                data-cy="device-model-name"
                v-model="form.name"
                @change="form.validate('name')"
                :disabled="disabled"
            />
	        </FormGroup>

	        <FormGroup :label="$t('forms.device_model.device_model_properties_form.device_type')" :error-text="form.errors?.device_type_id">
            <Dropdown
                data-cy="device-type-select"
                v-model="form.device_type_id"
                :options="deviceTypes"
                option-label="name"
                option-value="id"
                @change="form.validate('device_type_id')"
                :disabled="disabled"
            />
	        </FormGroup>

	        <FormGroup :label="$t('forms.device_model.device_model_properties_form.device_manufacturer')" :error-text="form.errors?.device_manufacturer_id">
		        <Dropdown
                        data-cy="device-manufacturer-select"
                        device-manufacturer-select
			        v-model="form.device_manufacturer_id"
			        :options="filteredDeviceManufacturers"
			        option-label="name"
			        option-value="id"
			        @change="form.validate('device_manufacturer_id')"
			        :placeholder="$t('common.na')"
			        show-clear
			        :disabled="disabled"
		        />
	        </FormGroup>

	        <FormGroup :label="$t('forms.device_model.device_model_properties_form.company')" :error-text="form.errors?.company_ids || form.errors?.company_id">
            <div class="flex flex-col gap-4">
              <div class="flex items-center gap-3">
                <InputSwitch
                    v-model="availableToAllCompanies"
                    :disabled="disabled"
                    data-cy="available-companies-switchbtn"
                />
                <span class="text-sm text-surface-500">{{ allCompaniesLabel }}</span>
              </div>
              <MultiSelect
                  v-if="!availableToAllCompanies"
                  v-model="form.company_ids"
                  :options="companiesStore.getCompanyOptions"
                  option-label="name"
                  option-value="id"
                  :disabled="disabled"
                  display="chip"
                  class="w-full"
                  @change="onCompanyIdsChange"
                  data-cy="company-name-multiselection-input"
              />
            </div>
	        </FormGroup>

	        <FormGroup :label="$t('forms.device_model.device_model_properties_form.image')">
	          <div class="flex flex-col gap-3">
	            <img
	                v-if="currentImageUrl"
	                :src="currentImageUrl"
	                class="h-24 w-auto object-contain rounded border border-surface-200"
	                alt="Device Model Image"
	            />
	            <div class="flex items-center gap-3">
	              <input
	                  ref="imageInput"
	                  type="file"
	                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
	                  class="hidden"
	                  @change="onImageFileSelected"
	                  :disabled="disabled"
	              />
	              <Button
	                  type="button"
	                  outlined
	                  size="small"
	                  :label="$t('forms.device_model.device_model_properties_form.choose_image')"
	                  @click="$refs.imageInput.click()"
	                  :disabled="disabled"
	              />
	              <span v-if="selectedImageFile" class="text-sm text-surface-500">{{ selectedImageFile.name }}</span>
	            </div>
	          </div>
	        </FormGroup>

        </div>
	    </template>

	    <template v-if="!disabled && (hasOnSaveListener || hasOnDeleteListener)" #footer>
		    <div class="flex justify-end space-x-3">
			    <Button data-cy="device-model-delete-btn" class="px-8" text severity="danger" v-if="hasOnDeleteListener && form.id" @click="$emit('on-delete', form)" :label="$t('forms.common.delete')"
			            :disabled="form.processing"/>
			    <Button data-cy="device-model-save-btn" class="px-8" v-if="hasOnSaveListener" @click="emitSave(selectedImageFile)" :label="$t('forms.common.save')"
			            :disabled="form.processing"/>
		    </div>
	    </template>
    </Card>
</template>

<script>
import { mapStores } from "pinia";
import { useCompaniesStore } from "@/stores/companies.js";
import { useDeviceManufacturersStore } from "@/stores/deviceManufacturers.js";
import { useDeviceTypesStore } from "@/stores/deviceTypes.js";
import FormGroup from "@/Components/FormGroup.vue";
import companySelectionValidation from "@/Mixins/companySelectionValidation.js";

export default {
    name: "DeviceModelPropertiesForm",
    components: {FormGroup},
    mixins: [companySelectionValidation],
    props: {
        form: {
            type: Object,
            required: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        errors: {
            type: Object,
        }
    },
    data() {
        return {
            selectedImageFile: null,
            currentImageUrl: null,
            availableToAllCompanies: true,
        };
    },
    computed: {
	      ...mapStores(useCompaniesStore),
	      ...mapStores(useDeviceManufacturersStore),
        ...mapStores(useDeviceTypesStore),
        hasOnSaveListener() {
            return !!(this.$attrs?.onOnSave);
        },
		    hasOnDeleteListener() {
			    return !!(this.$attrs?.onOnDelete);
		    },
        allCompaniesLabel() {
            return this.canSavePublicModel
                ? this.$t('forms.common.all')
                : this.$t('forms.common.all_accessible_companies');
        },
        canSavePublicModel() {
            return this.$auth.hasPermission('update:all-device-models');
        },
        companyRequiredMessage() {
            return this.$t('forms.device_model.device_model_properties_form.company_required');
        },
        deviceTypes() {
            const all = this.deviceTypesStore.getDeviceTypeOptions ?? [];
            let filtered;
            if (this.availableToAllCompanies || !this.selectedCompanyIds.length) {
                filtered = all;
            } else {
                filtered = all.filter(t => this.recordAvailableForSelectedCompanies(t));
            }
            // Add current value as fallback if it's not already in the list (e.g. from another company)
            const current = this.form.current_device_type;
            if (current && !filtered.some(t => t.id === current.id)) {
                filtered = [current, ...filtered];
            }
            return filtered;
        },
        filteredDeviceManufacturers() {
            const all = this.deviceManufacturersStore.getDeviceManufacturerOptions ?? [];
            let filtered;
            if (this.availableToAllCompanies || !this.selectedCompanyIds.length) {
                filtered = all;
            } else {
                filtered = all.filter(m => this.recordAvailableForSelectedCompanies(m));
            }
            // Add current value as fallback if it's not already in the list (e.g. from another company)
            const current = this.form.current_device_manufacturer;
            if (current && !filtered.some(m => m.id === current.id)) {
                filtered = [current, ...filtered];
            }
            return filtered;
        },
        selectedCompanyIds() {
            return Array.isArray(this.form.company_ids) ? this.form.company_ids : [];
        },
    },
    methods: {
        onCompanyIdsChange() {
            this.form.company_id = this.form.company_ids?.length === 1
                ? this.form.company_ids[0]
                : null;
            if (!this.validateCompanySelection()) {
                return;
            }
            this.clearUnavailableDependentFields();
            this.form.validate('company_ids');
        },
        setInitialCompanyIds() {
            if (!Array.isArray(this.form.company_ids)) {
                this.form.company_ids = this.form.company_id ? [this.form.company_id] : [];
            }
            this.availableToAllCompanies = !this.form.company_ids.length;
            this.form.apply_to_all_companies = this.availableToAllCompanies;
        },
        clearUnavailableDependentFields() {
            if (!this.deviceTypes.some(t => t.id === this.form.device_type_id)) {
                this.form.device_type_id = null;
            }

            if (!this.filteredDeviceManufacturers.some(m => m.id === this.form.device_manufacturer_id)) {
                this.form.device_manufacturer_id = null;
            }
        },
        recordAvailableForSelectedCompanies(record) {
            if (!this.selectedCompanyIds.length) {
                return true;
            }

            if (record.company_ids?.length) {
                return record.company_ids.some(companyId => this.selectedCompanyIds.includes(companyId));
            }

            return !record.company_id || this.selectedCompanyIds.includes(record.company_id);
        },
        onImageFileSelected(event) {
            this.selectedImageFile = event.target.files[0] || null;
            if (this.selectedImageFile) {
                this.currentImageUrl = URL.createObjectURL(this.selectedImageFile);
            }
        },
    },
    watch: {
        availableToAllCompanies(value) {
            this.form.apply_to_all_companies = value;
            if (value) {
                this.form.company_ids = [];
                this.form.company_id = null;
                this.clearCompanySelectionError();
                this.clearUnavailableDependentFields();
                return;
            }

            this.validateCompanySelection();
        },
        'form.image_url': {
            handler(newValue) {
                this.currentImageUrl = newValue;
            },
            immediate: true,
        },
    },
    mounted() {
	      this.setInitialCompanyIds();
	      this.companiesStore.indexCompanyOptions();
	      this.deviceManufacturersStore.indexDeviceManufacturerOptions({}, false);
        this.deviceTypesStore.indexDeviceTypeOptions({}, false);
    }
}
</script>

<style scoped>

</style>
